// Browser source. The build emits desktop.js beside this file; never edit the
// generated JavaScript directly.

import {
  choiceLabel,
  detectKeyboardLayout,
  LAYOUT_CHOICES,
  LayoutAnnouncer,
  type LayoutMapSource,
} from "./keyboard-layout.js";
import { ClipboardAssembly, PasteFlow } from "./clipboard.js";
import { GuestResize } from "./geometry.js";
import { Playback } from "./playback.js";
import { type AppClass, type Chord, appClassOf, detectClientOs, guestClipboardChord } from "./shortcuts.js";
import { KeyboardInput, localFullscreenChord } from "./keyboard-input.js";
import { CursorCache, type CursorImage, type CursorPicture, cursorPicture } from "./cursor.js";
import { parseControlMessage } from "./control-messages.js";
import { DeliveryReports } from "./reports.js";
import { VideoPlayout } from "./video-playout.js";
import { type FrameSource, VideoDiagnostics, watchFirstFrame } from "./video-watch.js";
import { formatBitrate } from "./stats.js";
import { LinkReadout } from "./link-readout.js";
import { type InputRecord, InputLanes } from "./input-lane.js";
import { GuestKeys } from "./keys.js";
import { probeNetwork } from "./network.js";
import { type ConnectionTimers, ViewerConnection } from "./connection.js";
import { RecoveryConsole, type RfbModule } from "./recovery.js";
import { PointerMotion } from "./pointer.js";
import { PointerInput, requestRawPointerLock } from "./pointer-input.js";
import { SessionController, type SessionTimers } from "./session.js";
import { notice } from "./errors.gen.js";
import { DOM } from "./dom.js";
import { ViewerPanel } from "./panel.js";
import {
  claimHandoff,
  exchangeDescription,
  handoffUrl,
  openBrowserSession,
  PendingRequest,
  postCandidates,
  resetHandoff,
} from "./gateway.js";
import { DiagnosticsQueue } from "./diagnostics.js";
import { ClientClipboard } from "./client-clipboard.js";
import { lockKeyboard, toggleFullscreen } from "./fullscreen.js";
import { readCapability } from "./capability.js";
import { ViewerLifecycle } from "./lifecycle.js";
import { TouchControls } from "./touch-controls.js";
import { layoutName } from "./text-keys.js";
import { LinkWatch } from "./link-watch.js";
import { ScreenWake, type WakeSentinel } from "./wake-lock.js";
import {
  KEYBOARD_STORAGE,
  loadKeyboardChoice,
  loadUiSize,
  UI_SIZE_STEPS,
  UI_SIZE_STORAGE,
  writePreference,
} from "./preferences.js";


// Input record types (input-lane.ts frames and sends them; pointer.ts sends
// the absolute and relative motion, pointer-input.ts buttons and the wheel).
const INPUT_KEY = 0;

// A tunnel blip (a NAT dropping its mapping, a relay hiccup) should cost a
// few seconds of freeze, not a manual RELINK: the cookie-authorized session
// survives, so a fresh offer on it reconnects without any new credential.
// A laptop that loses its Wi-Fi for a minute should find the desktop still
// there when it comes back, not a dead page. Relinks retry every couple of
// seconds for about five minutes before giving up.
const AUTO_RELINK_LIMIT = 150;
const AUTO_RELINK_DELAY_MS = 2_000;

// Chrome can sit in `disconnected` for half a minute before it reports
// `failed`, while the engine reaps a silent peer after fifteen seconds. A
// fresh offer on the same lease is cheap, so a stall this long is relinked.
const DISCONNECT_RELINK_MS = 3_000;

const CURSOR_CACHE_LIMIT = 64;

// A relinked picture stays on screen until the new stream's first frame,
// for at most this long; then the message card says what is going on.
const HOLD_PICTURE_MS = 30_000;

const browserTimers: SessionTimers & ConnectionTimers = {
  now: () => performance.now(),
  setTimeout: (callback, ms) => window.setTimeout(callback, ms),
  clearTimeout: (handle) => window.clearTimeout(handle),
  setInterval: (callback, ms) => window.setInterval(callback, ms),
  clearInterval: (handle) => window.clearInterval(handle),
};
// Which connect attempt is current, and when a stalled or failed link is
// tried again (session.ts).
const sessionController = new SessionController(
  browserTimers,
  { relinkLimit: AUTO_RELINK_LIMIT, relinkDelayMs: AUTO_RELINK_DELAY_MS, stallRelinkMs: DISCONNECT_RELINK_MS },
  () => lifecycle.connect(),
);
// The input, motion and keys channels of the current peer (input-lane.ts).
const inputLanes = new InputLanes(browserTimers);
// The engine's stats, the sampled link and the network probe (link-readout.ts).
const linkReadout = new LinkReadout();
// The picture's frames could go through a worker on their way to the
// decoder (video-playout.ts: paced out on a jittery link, a keyframe asked
// for as soon as frames come and none can be shown). None does: the hop cost
// every frame 8-10 ms in Chrome's jitter buffer and its pacing up to 16 more
// on a jittery link, and 30-40 ms in Safari's (lab, at the median from
// arrival to the screen: Chrome 6-10 ms without it against 17-32 with it,
// iPhone and iPad 12-13 against 42-52), for 3-5 points of smoothness; and
// libwebrtc itself asks for a keyframe 200 ms after nothing decodes.
const ScriptTransform = (globalThis as { RTCRtpScriptTransform?: new (worker: Worker, options: unknown) => unknown })
  .RTCRtpScriptTransform;
const videoPlayout = new VideoPlayout(
  {
    createWorker: () => null,
    createTransform: (worker, options) => new (ScriptTransform as NonNullable<typeof ScriptTransform>)(worker, options),
    now: () => performance.now(),
    visible: () => document.visibilityState === "visible",
    rttMs: () => linkReadout.link?.rttMs ?? 0,
    requestKeyframe: (stuck) => connection.sendControl({ t: "idr", why: "stuck", ...stuck }),
    setInterval: (callback, ms) => window.setInterval(callback, ms),
    delay: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  },
  true,
);
videoPlayout.prepare();
// Pointer positions and motion pacing (pointer.ts).
const pointerMotion = new PointerMotion(browserTimers, (type, a, b, c, droppable) => sendInput(type, a, b, c, droppable));
const pendingRequest = new PendingRequest();
// The current attempt's peer connection, channels and stream, the offer and
// answer and the connect watch (connection.ts); the page's part of each step
// is here.
const connection = new ViewerConnection({
  timers: browserTimers,
  now: () => performance.now(),
  createPeer: (config) => new RTCPeerConnection(config),
  createStream: () => new MediaStream(),
  isCurrent: (generation) => sessionController.isCurrent(generation),
  exchange: (session, offer, timeoutMs) =>
    exchangeDescription(session, offer, pendingRequest, timeoutMs, guestResize.forOffer()),
  postCandidates: (session, candidates) => void postCandidates(session, candidates),
  hooks: {
    diag,
    show: (stream) => {
      if (DOM.surface.srcObject !== stream) {
        DOM.surface.srcObject = stream;
      }
    },
    videoReceiver: (receiver) => videoPlayout.attach(receiver),
    firstVideo: (generation, startedAt) => {
      DOM.surface.hidden = false;
      watchFirstFrame(DOM.surface as unknown as FrameSource, startedAt, {
        now: () => performance.now(),
        isCurrent: () => sessionController.isCurrent(generation),
        guestFps: () => linkReadout.guestFps,
        report: reportFirstFrame,
      });
      // The surface just became measurable; ask for this display's pixels.
      guestResize.schedule();
    },
    state: (pc, generation) => {
      lifecycle.onConnectionState(pc, generation);
      // A stalled link says so on the frozen picture, quietly, and is moved
      // to a new path at once (link-watch.ts).
      if (pc.connectionState === "disconnected") {
        DOM.relinkNote.textContent = notice("viewer_view_link_stalled");
        DOM.relinkNote.hidden = false;
        linkWatch.check("disconnected");
      } else if (pc.connectionState === "connected") {
        if (DOM.viewport.dataset.holding !== "true") {
          DOM.relinkNote.hidden = true;
        }
        linkWatch.watch();
        // A phone or a tablet keeps its screen on while the desktop is live.
        screenWake.setLive(touchDevice());
        touch.live();
      }
    },
    input: (open) => {
      DOM.inputState.textContent = open ? pointerLocked() ? "LOCKED" : "READY" : "STANDBY";
      DOM.inputLamp.dataset.live = String(open);
    },
    lanes: (_pc, input, motion, keys) => {
      guestClipboardReady = false;
      pointer.setRelative(false);
      touch.setGuestRelative(false);
      inputLanes.bind(input, motion, keys);
    },
    controlOpen: () => {
      flushDiag();
      // The window's size as it opened goes at once (the host applies a
      // viewer's first size without its debounce too); only later resize
      // series wait for RESIZE_DEBOUNCE_MS.
      guestResize.send();
      void layoutAnnouncer.announce();
    },
    controlMessage: onControlMessage,
    bound: () => {
      videoDiagnostics.arm();
      deliveryReports.start();
    },
    noDecoder: (offer) => lifecycle.stopWithoutDecoder(offer),
    signaling: () => {
      DOM.viewState.textContent = notice("viewer_view_signaling");
    },
    answered: (codec) => {
      if (codec !== null) {
        DOM.codec.textContent = `${codec} + OPUS`;
      }
      DOM.viewState.textContent = notice("viewer_view_connecting_media");
    },
    retry: (left) => {
      diag("network", { verdict: "retry", left });
      lifecycle.connect();
    },
    blocked: (pc, generation) => {
      void lifecycle.showNetworkBlocked(pc, generation);
    },
  },
});
// The emergency console in noVNC (recovery.ts).
const recoveryConsole = new RecoveryConsole({
  load: (path) => import(path) as Promise<RfbModule>,
  stage: DOM.rfbStage,
  isCurrent: (generation) => sessionController.isCurrent(generation),
  connected: (client) => {
    const capability = lifecycle.capability;
    if (capability === null) {
      return;
    }
    DOM.message.hidden = true;
    releasePicture();
    DOM.inputState.textContent = "RFB";
    panel.setConnection("recovery", notice("viewer_status_recovery_live", { name: capability.name.toUpperCase() }));
    client.focus();
  },
  ended: (reason) => lifecycle.fail(reason),
});
let uiSize = loadUiSize();
// The guest's mode for this viewport, asked on the control channel (geometry.ts).
const guestResize = new GuestResize({
  timers: browserTimers,
  channel: () => connection.control,
  // Measure the CONTAINER, never the video element: the element's own box
  // follows the incoming stream's aspect, so measuring it asks the guest
  // for whatever the guest already is — a feedback loop that walked the
  // resolution down (1398 → 1128 → …) and left a small guest stretched
  // huge across a large window. The container is sized to the visible
  // viewport (fitToVisibleViewport).
  measure: () => ({ width: DOM.viewport.clientWidth, height: DOM.viewport.clientHeight, uiSize }),
});
let keyboardChoice = loadKeyboardChoice();
// Tells the guest which keyboard the person types on (keyboard-layout.ts).
const layoutAnnouncer = new LayoutAnnouncer({
  detect: () => detectKeyboardLayout(
    navigator.language || "en-US",
    (navigator as { keyboard?: LayoutMapSource }).keyboard,
    isMac,
  ),
  mac: isMac,
  choice: () => keyboardChoice,
  channel: () => connection.control,
  show: (readout) => {
    DOM.keyboardState.textContent = readout;
  },
  diag,
});
const cursorCache = new CursorCache<CursorPicture>(CURSOR_CACHE_LIMIT);

// Shortcuts follow the client's operating system (shortcuts.ts): on a Mac,
// Command is never forwarded as Super; Command chords become the Linux
// chord the focused guest window expects, which kiste-desktop reports.
const CLIENT_OS = detectClientOs(
  (navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform
    ?? navigator.platform,
  navigator.userAgent,
);
const ON_MAC = CLIENT_OS === "mac";
// The keys the guest holds, modifier flags and translated chords (keys.ts).
const guestKeys = new GuestKeys(ON_MAC, (code, down) => sendInput(INPUT_KEY, code, down ? 1 : 0, 0));
let focusedApp: AppClass = "app";
// Set by the first focus report of a connection: only an engine and guest
// agent that speak the clipboard protocol send one, and nothing of the
// person's clipboard is sent to a side that does not (an older engine would
// write it into its verbatim trace of viewer messages).
let guestClipboardReady = false;

// Clipboard (clipboard.ts). A paste chord carries the client's clipboard to
// the guest first: the browser hands it over in a paste event on a hidden
// text field, no permission prompt, and the paste keystroke waits for the
// guest's ack. A copy chord arms a clipboard write inside the keystroke's
// own user activation that completes when the guest's text arrives.
const clipboardAssembly = new ClipboardAssembly();
// This computer's clipboard (client-clipboard.ts).
const clientClipboard = new ClientClipboard({
  synced: (text) => pasteFlow.markSynced(text),
  diag: (ev, bytes) => diag("clip", { ev, bytes }),
});
const clipboardSink = document.createElement("textarea");
const pasteFlow = new PasteFlow<Chord, InputRecord>({
  timers: browserTimers,
  sendControl: (message) => !viewOnly && connection.sendControl(message),
  tapChords: (chords) => {
    if (inputReady()) {
      for (const chord of chords) {
        guestKeys.tapChord(chord);
      }
    }
  },
  release: (held) => {
    for (const [type, a, b, c] of held) {
      sendInput(type, a, b, c);
    }
  },
  flushClientWrite: () => {
    if (!clientClipboard.hasPendingWrite) {
      return false;
    }
    clientClipboard.flushPending();
    return true;
  },
  restoreFocus: () => {
    if (document.activeElement === clipboardSink && !DOM.surface.hidden) {
      DOM.surface.focus({ preventScroll: true });
    }
  },
  diag: (ev) => diag("clip", { ev }),
});

// Key events (keyboard-input.ts): Command chords, a lone Command as Super,
// and the paste and copy chords that carry the clipboard.
const keyboard = new KeyboardInput<KeyboardEvent>({
  guestKeys,
  clientOs: CLIENT_OS,
  inputReady,
  inputTarget,
  flushClipboard: () => clientClipboard.flushPending(),
  armCopy: () => clientClipboard.armCopy(),
  beginPaste,
  focusedApp: () => focusedApp,
});

/** The person's SOUND choice ("on", "off"), kept across loads. */
const SOUND_STORAGE = "kiste.viewer.sound";

// Pointer and wheel events (pointer-input.ts).
const pointer = new PointerInput({
  surface: DOM.surface,
  guestKeys,
  onMac: ON_MAC,
  motion: pointerMotion,
  inputReady,
  sendInput,
  pointerLocked,
  requestPointerLock: () => requestRawPointerLock(DOM.surface),
  exitPointerLock: () => document.exitPointerLock(),
  beforeClick: () => {
    clientClipboard.flushPending();
    keyboard.interrupt();
  },
  showMode: (relative) => {
    DOM.pointerState.textContent = relative ? "RELATIVE" : "ABSOLUTE";
  },
  // Through the picture's local zoom (touch-controls.ts), where a pinch put it.
  locate: (event) => touch.locate(event),
  viewScale: () => touch.zoom.transform.scale,
  pinch: (factor, x, y) => touch.trackpadPinch(factor, x, y),
});

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

// Fingers, the picture's zoom and the on-screen keyboard (touch-controls.ts).
const touch = new TouchControls({
  pointer,
  motion: pointerMotion,
  guestKeys,
  inputReady,
  layout: () => layoutName(layoutAnnouncer.layout ?? { xkb: "us", variant: "" }),
  paste: (text, done) => {
    // What no key of the guest's layout types (an emoji, another script)
    // goes through its clipboard, as a paste chord would carry it.
    if (!guestClipboardReady) {
      done(false);
      return;
    }
    pasteFlow.pasteText(text, [guestClipboardChord("paste", focusedApp)], done);
  },
  armCopy: () => clientClipboard.armCopy(),
  pasteChord: (chord) => {
    const fallback = () => {
      if (inputReady()) {
        guestKeys.tapChord(chord);
      }
    };
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (!guestClipboardReady || typeof clipboard?.readText !== "function") {
      fallback();
      return;
    }
    // The browser asks the person (Safari's Paste bubble); a refusal pastes
    // what the guest's clipboard holds.
    clipboard.readText().then((text) => {
      if (text === "") {
        fallback();
        return;
      }
      pasteFlow.begin([chord]);
      pasteFlow.pasted(text, true);
    }, fallback);
  },
  refit: () => fitToVisibleViewport(),
  physicalKey: (event, down) => {
    if (down) {
      keyboard.onKeyDown(event);
    } else {
      keyboard.onKeyUp(event);
    }
  },
  timers: browserTimers,
});

function isMac(): boolean {
  const agentData = (navigator as { userAgentData?: { platform?: string } }).userAgentData;
  return /mac/i.test(agentData?.platform ?? "") || /mac/i.test(navigator.platform);
}

// The status line, the message card and the overlay's blanks (panel.ts).
const panel = new ViewerPanel(DOM, document);

// A view-only link (capability.ts): the page sends no input and no
// clipboard; the stream engine drops whatever arrives anyway.
let viewOnly = false;

function sendInput(type: number, a: number, b: number, c: number, droppable = false): void {
  if (viewOnly) {
    return;
  }
  // A paste in flight holds keys and buttons back so they reach the guest
  // after the paste keystroke, in the order they were pressed.
  if (!droppable && pasteFlow.hold([type, a, b, c])) {
    return;
  }
  if (droppable) {
    inputLanes.sendDroppable([type, a, b, c]);
  } else {
    inputLanes.sendSequenced([type, a, b, c]);
  }
}

function inputReady(): boolean {
  return inputLanes.ready();
}

function releaseAllInput(): void {
  pasteFlow.finish(false);
  keyboard.reset();
  touch.releaseAll();
  pointer.releaseAll();
}

function pointerLocked(): boolean {
  return document.pointerLockElement === DOM.surface;
}

function inputTarget(event: Event): boolean {
  return event.target === DOM.surface || event.target === DOM.viewport
    || event.target === clipboardSink || event.target === DOM.softInput;
}

// --- Clipboard --------------------------------------------------------------

/** Starts a paste: wait for the browser's paste event, then the guest. */
function beginPaste(chords: Chord[]): void {
  pasteFlow.begin(chords);
  // The paste runs against the focused element once this handler returns.
  clipboardSink.value = "";
  clipboardSink.focus({ preventScroll: true });
}

function onPaste(event: ClipboardEvent): void {
  event.preventDefault();
  pasteFlow.pasted(event.clipboardData?.getData("text/plain") ?? "", guestClipboardReady);
}

function applyCursor(cursor: CursorImage): void {
  const picture = cursorCache.get(cursor.serial) ?? cursorPicture(cursor);
  if (picture === null) {
    return;
  }
  cursorCache.set(cursor.serial, picture);
  DOM.surface.style.cursor = picture.css;
  touch.setCursor(picture);
}

function onControlMessage(event: MessageEvent<unknown>): void {
  const message = parseControlMessage(event.data);
  if (message === null) {
    return;
  }
  switch (message.kind) {
    case "cursor":
      applyCursor(message.cursor);
      break;
    case "stats": {
      const rtt = linkReadout.engineStats(message.payload);
      if (rtt !== null) {
        DOM.statRtt.textContent = rtt;
      }
      break;
    }
    case "clip": {
      const text = clipboardAssembly.push(message.payload);
      if (text !== null) {
        clientClipboard.fromGuest(text);
      }
      break;
    }
    case "path": {
      // DOWN is the latest probe train, PATH the best of the last minute.
      const atLeast = message.atLeast ? "≥ " : "";
      if (message.kbit !== null) {
        DOM.statPath.textContent = `${atLeast}${formatBitrate(message.kbit * 1000)}`;
      }
      if (message.nowKbit !== null) {
        DOM.statDown.textContent = `${atLeast}${formatBitrate(message.nowKbit * 1000)}`;
      }
      break;
    }
    case "pointer":
      pointer.setRelative(message.relative);
      touch.setGuestRelative(message.relative);
      break;
    case "clipack":
      pasteFlow.acknowledged(message.id, message.ok);
      break;
    case "focus":
      focusedApp = appClassOf(message.instance, message.windowClass);
      guestClipboardReady = true;
      DOM.viewport.dataset.focusedApp = focusedApp;
      break;
    case "end":
      if (message.network !== null) {
        lifecycle.networkRefused(message.network);
      } else if (message.replaced) {
        lifecycle.replaced();
      } else {
        lifecycle.engineEnded();
      }
      break;
  }
}

/**
 * Sizes the workspace to what the person actually sees. On a phone the
 * layout viewport reaches behind Safari's toolbars, and a picture sized to
 * it lost its bottom edge behind them; the visual viewport is the visible
 * part, and it changes as the toolbars collapse, on rotation and while the
 * on-screen keyboard is up.
 */
function fitToVisibleViewport(): void {
  const visual = window.visualViewport;
  const style = DOM.workspace.style;
  if (visual === null || visual === undefined) {
    style.removeProperty("width");
    style.removeProperty("height");
    return;
  }
  // The on-screen keyboard covers the bottom without taking the page's
  // height: the guest keeps its mode while someone types, and the picture
  // slides up instead (touch-controls.ts).
  const covered = touch.fit(visual.width, visual.height);
  style.width = `${Math.round(visual.width)}px`;
  style.height = `${Math.round(visual.height + covered)}px`;
  style.transform = `translate(${Math.round(visual.offsetLeft)}px, ${Math.round(visual.offsetTop)}px)`;
  touch.place(visual);
  if (covered === 0) {
    guestResize.schedule();
  }
}

// A relink of a live picture (a phone that changed networks, an engine
// that ended the session) keeps the last frame up, with a quiet note, instead
// of a black card for the second or two the new link takes.
let holdTimer = 0;

function holdPicture(): void {
  const surface = DOM.surface;
  // A picture on screen, live or stalled; not after an attempt that never
  // showed one (the held picture of the attempt before stays).
  if (surface.hidden || surface.videoWidth < 1 || surface.videoHeight < 1) {
    return;
  }
  const still = DOM.still;
  try {
    still.width = surface.videoWidth;
    still.height = surface.videoHeight;
    still.getContext("2d")?.drawImage(surface, 0, 0);
  } catch {
    // A frame the browser will not hand over: the card shows instead.
    return;
  }
  still.style.transform = surface.style.transform;
  still.hidden = false;
  DOM.relinkNote.textContent = notice("viewer_view_relinking");
  DOM.relinkNote.hidden = false;
  DOM.viewport.dataset.holding = "true";
  window.clearTimeout(holdTimer);
  holdTimer = window.setTimeout(releasePicture, HOLD_PICTURE_MS);
}

function releasePicture(): void {
  window.clearTimeout(holdTimer);
  holdTimer = 0;
  DOM.still.hidden = true;
  DOM.relinkNote.hidden = true;
  delete DOM.viewport.dataset.holding;
}

function reportFirstFrame(elapsedMs: number, fps: number): void {
  releasePicture();
  DOM.viewport.dataset.firstFrameUnixMs = String(Date.now());
  DOM.viewport.dataset.firstFrameElapsedMs = String(elapsedMs);
  DOM.viewport.dataset.firstFrameFps = String(fps);
  console.info(
    `KISTE_DESKTOP_FIRST_NATIVE_FRAME protocol=dev.kiste.desktop-native.v1 elapsed_ms=${elapsedMs} fps=${fps} relay=false decoded=true`,
  );
}

// The picture's playback and the desktop's sound (playback.ts).
const playback = new Playback({
  video: DOM.surface,
  button: DOM.sound,
  diag,
  refused: () => lifecycle.askForGesture(),
  memory: {
    read: () => readStorage(SOUND_STORAGE),
    write: (choice) => writePreference(SOUND_STORAGE, choice),
  },
});

// Diagnostics travel over the control channel into the server's session
// trace, queued until the channel actually opens so the record survives a
// transport that comes up slowly (which is itself a finding).
// Records for the host's trace on the control channel (diagnostics.ts).
const diagnostics = new DiagnosticsQueue(() => connection.control);
// The view report a second (reports.ts).
const deliveryReports = new DeliveryReports({
  channel: () => connection.control,
  peer: () => connection.peer,
  held: () => ({ seq: inputLanes.sequenced(), keys: guestKeys.held(), buttons: pointer.pressedButtons }),
  sendControl: (message) => connection.sendControl(message),
  sampleLink: (shownFps) => lifecycle.sampleLink(shownFps),
  playout: () => videoPlayout,
});
function diag(kind: string, extra: Record<string, unknown> = {}): void {
  diagnostics.push(kind, extra);
}
function flushDiag(): void {
  diagnostics.flush();
}
// The video element's events and this browser's window for the trace
// (video-watch.ts).
const videoDiagnostics = new VideoDiagnostics(DOM.surface, diag, () => ({
  ua: navigator.userAgent.slice(0, 120),
  dpr: window.devicePixelRatio,
  vw: window.innerWidth,
  vh: window.innerHeight,
}));

// The capability, the browser session and the stream on it, relinks, the
// sign-in handoff and the emergency console (lifecycle.ts).
const lifecycle = new ViewerLifecycle({
  dom: DOM,
  panel,
  session: sessionController,
  connection,
  link: linkReadout,
  recovery: recoveryConsole,
  pending: pendingRequest,
  openSession: (capability, isCurrent, booting) =>
    openBrowserSession(capability, pendingRequest, isCurrent, booting, takeBoot(capability.name, capability.token !== "")),
  probe: () => probeNetwork(
    (url) => fetch(url, { cache: "no-store", credentials: "omit" }),
    () => performance.now(),
    "/desktop/manifest.webmanifest",
  ),
  claimHandoff: () => claimHandoff(() => sessionStorage, () => Date.now()),
  signIn: (capability) => location.replace(handoffUrl(capability)),
  openConsole: () => {
    window.open("https://console.kiste.run/app", "_blank", "noopener");
    resetHandoff(() => sessionStorage);
  },
  userAgent: navigator.userAgent,
  now: () => performance.now(),
  pointerLocked,
  moving: () => linkWatch.moving,
  play: () => void playback.play(),
  teardown: () => {
    holdPicture();
    linkWatch.cancel();
    screenWake.setLive(false);
    guestResize.cancel();
    releaseAllInput();
    pointer.setRelative(false);
    touch.setGuestRelative(false);
    deliveryReports.stop();
    inputLanes.close();
    pointerMotion.cancel();
    cursorCache.clear();
    guestResize.forget();
    pointer.resetWheel();
  },
  diag,
  viewOnly: (only) => {
    viewOnly = only;
    if (only) {
      DOM.root.dataset.viewOnly = "";
    } else {
      delete DOM.root.dataset.viewOnly;
    }
  },
});

// The screen of a touch device stays on while the desktop is live and in
// use (wake-lock.ts).
const screenWake = new ScreenWake({
  request: typeof (navigator as { wakeLock?: unknown }).wakeLock === "object"
    ? () => (navigator as unknown as { wakeLock: { request(type: "screen"): Promise<WakeSentinel> } }).wakeLock.request("screen")
    : null,
  visible: () => document.visibilityState === "visible",
  now: () => performance.now(),
  timers: browserTimers,
});

function touchDevice(): boolean {
  return window.matchMedia?.("(pointer: coarse)").matches === true || !DOM.touchTools.hidden;
}

// A network change, the page coming back into view or back online: a link
// that delivers nothing any more is relinked at once (link-watch.ts).
const linkWatch = new LinkWatch({
  timers: browserTimers,
  now: () => performance.now(),
  visible: () => document.visibilityState === "visible",
  peer: () => connection.peer,
  canRelink: () => lifecycle.browserSession !== null && lifecycle.capability !== null && !lifecycle.refused,
  restart: () => lifecycle.restartLink(),
  relink: () => lifecycle.linkLost(),
  replaced: () => lifecycle.replaced(),
  // The picture stays while the link moves; a quiet note says why it froze.
  moving: (moving) => {
    if (moving) {
      DOM.relinkNote.textContent = notice("viewer_view_relinking");
      DOM.relinkNote.hidden = false;
    } else if (DOM.viewport.dataset.holding !== "true") {
      DOM.relinkNote.hidden = true;
    }
  },
  diag,
});

/**
 * The session request index.html sent as the page arrived, when it was for
 * this capability; once (a relink asks anew).
 */
function takeBoot(name: string, withToken: boolean): Promise<Response> | null {
  const page = window as { __kisteBoot?: { name: string; token: boolean; response: Promise<Response> } };
  const boot = page.__kisteBoot;
  delete page.__kisteBoot;
  if (boot === undefined || boot.name !== name || boot.token !== withToken) {
    // A request nobody reads still ends; its rejection is not an error here.
    void boot?.response.catch(() => {});
    return null;
  }
  return boot.response;
}

function populateKeyboardChoices(): void {
  DOM.keyboard.replaceChildren(
    ...LAYOUT_CHOICES.map((choice) => {
      const option = document.createElement("option");
      option.value = choice;
      option.textContent = choiceLabel(choice);
      option.selected = choice === keyboardChoice;
      return option;
    }),
  );
}

function updateUiSizeLabel(): void {
  const label = DOM.uiSize.querySelector("small");
  if (label !== null) {
    label.textContent = `${Math.round(uiSize * 100)}%`;
  }
  DOM.uiSize.title = `UI size ${Math.round(uiSize * 100)}% — select to cycle`;
}

// On a touch device the whole page, so the touch tools and the extra keys
// stay on screen; on a computer the picture alone.
DOM.fullscreen.addEventListener("click", () => {
  void toggleFullscreen(touchDevice() ? document.documentElement : DOM.viewport);
});
DOM.touchFullscreen.addEventListener("click", () => { void toggleFullscreen(document.documentElement); });
// FIT always asks, even for the size this viewer asked for before: with
// several viewers on one desktop, another one may have taken the guest's
// mode since, and FIT is how this one takes it back.
DOM.resize.addEventListener("click", () => {
  // The whole picture too, wherever a pinch left it.
  touch.zoom.reset();
  guestResize.forget();
  guestResize.schedule();
});
DOM.uiSize.addEventListener("click", () => {
  const index = UI_SIZE_STEPS.indexOf(uiSize);
  uiSize = UI_SIZE_STEPS[(index + 1) % UI_SIZE_STEPS.length] ?? 1;
  writePreference(UI_SIZE_STORAGE, String(uiSize));
  updateUiSizeLabel();
  guestResize.send();
});
DOM.keyboard.addEventListener("change", () => {
  keyboardChoice = LAYOUT_CHOICES.includes(DOM.keyboard.value) ? DOM.keyboard.value : "auto";
  writePreference(KEYBOARD_STORAGE, keyboardChoice);
  void layoutAnnouncer.announce();
});
DOM.sound.addEventListener("click", () => playback.toggle());
DOM.reconnect.addEventListener("click", () => lifecycle.connect());
DOM.messageAction.addEventListener("click", () => lifecycle.messageAction());
DOM.recoveryButton.addEventListener("click", () => lifecycle.recoverManually());
// Every click belongs to the guest: a double-click opens a file or an app
// there, and it once also toggled this window to fullscreen. Fullscreen is
// the toolbar button and Ctrl+Alt+F only.
DOM.viewport.addEventListener("dblclick", (event: MouseEvent) => { event.preventDefault(); });
// Fingers go to the gestures (touch-controls.ts), a mouse and a pen to the
// pointer.
DOM.surface.addEventListener("pointerdown", (event) => {
  if (!touch.pointerDown(event)) {
    pointer.onPointerDown(event);
  }
});
DOM.surface.addEventListener("pointerup", (event) => {
  if (!touch.pointerUp(event)) {
    pointer.onPointerUp(event);
  }
});
// A key typed at the desktop is a gesture too (every key but Escape is one
// for Chrome and Safari): a session started or reloaded by typing had its
// sound muted until the first click. Captured, so the on-screen keyboard's
// field counts as well.
DOM.viewport.addEventListener("keydown", (event: KeyboardEvent) => {
  if (event.key !== "Escape") {
    playback.gesture();
  }
}, { capture: true });
// The first press on the desktop turns the sound on, inside the event that
// counts as the person's gesture: a mouse's press, a finger's or a pen's
// lift (HTML's activation-triggering events; Safari refuses sound, or pauses
// the picture, when it is asked from a finger's touch-down).
DOM.surface.addEventListener("pointerdown", (event) => {
  if (event.pointerType === "mouse") {
    playback.gesture();
  }
});
DOM.surface.addEventListener("pointerup", (event) => {
  if (event.pointerType !== "mouse") {
    playback.gesture();
  }
});
DOM.surface.addEventListener("pointercancel", (event) => {
  if (!touch.pointerCancel(event)) {
    releaseAllInput();
  }
});
// pointerrawupdate delivers samples ahead of frame alignment where the
// browser supports it — up to a display frame less input latency. The
// regular event stays as the fallback path. The feature test runs on an
// untyped view so TypeScript's `in` narrowing cannot void the element type.
const supportsRawPointer =
  "onpointerrawupdate" in (DOM.surface as unknown as Record<string, unknown>);
DOM.surface.addEventListener(
  (supportsRawPointer ? "pointerrawupdate" : "pointermove") as "pointermove",
  (event: PointerEvent) => {
    if (!touch.pointerMove(event)) {
      pointer.onPointerMove(event);
    }
  },
);
// A new guest mode moves the trackpad pointer's bounds.
DOM.surface.addEventListener("resize", () => touch.pictureChanged());
// Safari's own pinch (its gesture events) and the touch defaults behind the
// pointer events (a double tap's zoom, the text magnifier, the compatibility
// mouse events that would take the focus from the on-screen keyboard's
// field) stay off the picture.
document.addEventListener("gesturestart", (event) => event.preventDefault());
// Safari's pinch on a Mac's trackpad comes as these gesture events (Chrome
// and Firefox send a wheel with Ctrl): it zooms the picture here. On an
// iPhone or iPad the same events follow the fingers, which the touch
// gestures already zoom with.
let gestureScale = 1;
document.addEventListener("gesturestart", (event) => {
  gestureScale = (event as unknown as { scale: number }).scale || 1;
});
document.addEventListener("gesturechange", (event) => {
  event.preventDefault();
  const gesture = event as unknown as { scale: number; clientX: number; clientY: number };
  if (touch.touching || !(gesture.scale > 0)) {
    return;
  }
  touch.trackpadPinch(gesture.scale / gestureScale, gesture.clientX, gesture.clientY);
  gestureScale = gesture.scale;
});
DOM.viewport.addEventListener("touchstart", (event: TouchEvent) => {
  if (event.target === DOM.surface) {
    event.preventDefault();
  }
}, { passive: false });
DOM.surface.addEventListener("wheel", (event) => pointer.onWheel(event), { passive: false });
DOM.surface.addEventListener("contextmenu", (event: Event) => { event.preventDefault(); });
DOM.viewport.addEventListener("keydown", (event) => keyboard.onKeyDown(event));
DOM.viewport.addEventListener("keyup", (event) => keyboard.onKeyUp(event));
// The paste chord's target: off screen, out of the tab order, never read
// aloud. It holds focus only between a paste keystroke and its paste event.
clipboardSink.tabIndex = -1;
clipboardSink.setAttribute("aria-hidden", "true");
clipboardSink.setAttribute("autocomplete", "off");
clipboardSink.spellcheck = false;
Object.assign(clipboardSink.style, {
  height: "1px",
  left: "-10000px",
  opacity: "0",
  pointerEvents: "none",
  position: "absolute",
  top: "0",
  width: "1px",
});
DOM.viewport.append(clipboardSink);
clipboardSink.addEventListener("paste", onPaste);
document.addEventListener("pointerlockchange", () => {
  const locked = pointerLocked();
  // Escape out of a game: buttons held through it are let go. Motion stays
  // relative while the game holds the pointer; the next click locks again.
  if (!locked && pointer.relative) {
    releaseAllInput();
  }
  DOM.inputState.textContent = locked ? "LOCKED" : inputReady() ? "READY" : "STANDBY";
});
document.addEventListener("fullscreenchange", () => {
  lockKeyboard(document.fullscreenElement !== null);
  if (!DOM.surface.hidden && !touch.keyboardOpen) {
    DOM.surface.focus();
  }
  guestResize.schedule();
});
// The guest must always match this display: every reconnect and every
// window-size change re-asks. Without these two hooks the resize only ever
// fired from the SIZE button — a fresh-booted guest sat at its default
// resolution, the picture rendered at the wrong size, and the absolute
// pointer math aimed at coordinates the guest did not have.
window.addEventListener("resize", fitToVisibleViewport);
window.visualViewport?.addEventListener("resize", fitToVisibleViewport);
window.visualViewport?.addEventListener("scroll", fitToVisibleViewport);
// A phone browser has no element fullscreen (iPhone Safari); the button
// would do nothing there. A home-screen launch is the fullscreen it gets.
DOM.touchFullscreen.hidden = document.fullscreenEnabled !== true;
if (document.fullscreenEnabled !== true) {
  DOM.fullscreen.hidden = true;
}
window.addEventListener("online", () => {
  linkWatch.check("online");
  lifecycle.online();
});
// Android's Chrome says when the device changes networks (Wi-Fi to cellular,
// one Wi-Fi to another); every browser when the page comes back into view
// or out of the back-forward cache. A move to another kind of network leaves
// the old path behind for certain; other changes (one Wi-Fi to another, the
// browser's own estimates) are checked first.
const networkInfo = (navigator as { connection?: EventTarget & { readonly type?: string } }).connection;
let networkType = networkInfo?.type;
networkInfo?.addEventListener?.("change", () => {
  const type = networkInfo.type;
  const moved = type !== undefined && networkType !== undefined && type !== networkType;
  networkType = type;
  linkWatch.check(moved ? "network-type" : "network");
});
document.addEventListener("visibilitychange", () => {
  screenWake.visibilityChanged();
  if (document.visibilityState === "visible") {
    linkWatch.check("visible");
  }
});
for (const kind of ["pointerdown", "keydown"] as const) {
  window.addEventListener(kind, () => screenWake.input(), { capture: true, passive: true });
}
window.addEventListener("pageshow", (event: PageTransitionEvent) => {
  if (event.persisted) {
    linkWatch.check("pageshow");
  }
});
window.addEventListener("blur", () => {
  releaseAllInput();
  clientClipboard.forget();
});
window.addEventListener("keydown", (event: KeyboardEvent) => {
  if (localFullscreenChord(event)) {
    event.preventDefault();
    void toggleFullscreen();
  }
});
// The lease deliberately survives the page: revoking on pagehide made every
// reload fatal — the browser came back holding a cookie for a session that
// no longer existed and had no way to mint another. Abandoned leases are
// bounded (24 h expiry, 32 per browser, renew replaces same-machine ones)
// and the engine's newest-viewer-wins rule already retires the stream side.
// An accidental ⌘R mid-stream still deserves a speed bump; the browser shows
// its leave-site prompt only while the picture is actually live.
window.addEventListener("beforeunload", (event: BeforeUnloadEvent) => {
  if (DOM.viewport.dataset.live === "true") {
    event.preventDefault();
  }
});

pointer.setRelative(false);
fitToVisibleViewport();
playback.start();
populateKeyboardChoices();
updateUiSizeLabel();
void layoutAnnouncer.announce();

lifecycle.start(readCapability);
